import React, { useEffect, useRef, useMemo } from 'react';
import Chart from 'chart.js/auto';

const IndustryChart = ({ alumniData = [] }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // Process alumni data to get industry distribution
  const industryData = useMemo(() => {
    // Count alumni by industry
    const industryCount = {};
    
    alumniData.forEach(alumnus => {
      if (alumnus.industry) {
        // Normalize industry names
        let industry = alumnus.industry;
        
        // Group similar industries
        if (industry.includes('Tech') || industry.includes('Software') || industry.includes('IT')) {
          industry = 'Technology';
        } else if (industry.includes('Finance') || industry.includes('Banking') || industry.includes('Insurance')) {
          industry = 'Finance';
        } else if (industry.includes('Health') || industry.includes('Medical') || industry.includes('Pharma')) {
          industry = 'Healthcare';
        } else if (industry.includes('Retail') || industry.includes('Commerce') || industry.includes('Consumer')) {
          industry = 'Retail';
        } else if (industry.includes('Manufacturing') || industry.includes('Industrial')) {
          industry = 'Manufacturing';
        } else if (industry.includes('Education') || industry.includes('Learning')) {
          industry = 'Education';
        } else if (industry.includes('Media') || industry.includes('Entertainment')) {
          industry = 'Media & Entertainment';
        } else if (industry.includes('Consulting') || industry.includes('Services')) {
          industry = 'Consulting';
        } else {
          industry = 'Other';
        }
        
        industryCount[industry] = (industryCount[industry] || 0) + 1;
      }
    });
    
    // Sort industries by count (descending)
    const sortedIndustries = Object.entries(industryCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6); // Take top 6 industries
    
    // If we have fewer than 6 industries, add some defaults
    if (sortedIndustries.length < 6) {
      const defaultIndustries = ['Technology', 'Finance', 'Healthcare', 'Manufacturing', 'Retail', 'Consulting'];
      defaultIndustries.forEach(industry => {
        if (!industryCount[industry] && sortedIndustries.length < 6) {
          sortedIndustries.push([industry, 0]);
        }
      });
    }
    
    return {
      labels: sortedIndustries.map(item => item[0]),
      data: sortedIndustries.map(item => item[1])
    };
  }, [alumniData]);

  useEffect(() => {
    // Destroy existing chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }
    
    const data = {
      labels: industryData.labels,
      datasets: [
        {
          label: 'Lead Qualification by Industry',
          data: industryData.data,
          backgroundColor: [
            'rgba(54, 162, 235, 0.6)',
            'rgba(75, 192, 192, 0.6)',
            'rgba(153, 102, 255, 0.6)',
            'rgba(255, 159, 64, 0.6)',
            'rgba(255, 99, 132, 0.6)',
            'rgba(128, 128, 128, 0.6)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)',
            'rgba(255, 159, 64, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(128, 128, 128, 1)'
          ],
          borderWidth: 1
        }
      ]
    };

    // Create new chart
    const ctx = chartRef.current.getContext('2d');
    chartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Number of Leads'
            }
          },
          x: {
            title: {
              display: true,
              text: 'Industry'
            }
          }
        },
        plugins: {
          legend: {
            display: false
          }
        }
      }
    });

    // Cleanup function
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [industryData]);

  return (
    <div style={{ height: '300px' }}>
      <canvas ref={chartRef}></canvas>
    </div>
  );
};

export default IndustryChart;
